import { getEnv } from "@istari-frontend/shared";

import { EXPERIMENTAL_FEATURE_LIST } from "./registry";
import type { ExperimentalFeature, ExperimentalFeatureId } from "./registry";

export type ExperimentalFeatureEnv = {
  available: readonly string[];
  defaultOn: readonly string[];
  locked: readonly string[];
};

export type ExperimentalFeatureState =
  { available: false; locked: false; enabled: false } | { available: true; locked: boolean; enabled: boolean };

export function readExperimentalFeatureEnv(): ExperimentalFeatureEnv {
  return {
    available: parseIdList(getEnv("VITE_EXPERIMENTAL_AVAILABLE")),
    defaultOn: parseIdList(getEnv("VITE_EXPERIMENTAL_DEFAULT_ON")),
    locked: parseIdList(getEnv("VITE_EXPERIMENTAL_LOCKED")),
  };
}

export function resolveExperimentalFeature(
  feature: ExperimentalFeature,
  env: ExperimentalFeatureEnv,
  storedChoice: boolean | null,
): ExperimentalFeatureState {
  if (feature.gated && !env.available.includes(feature.id)) {
    return { enabled: false, locked: false, available: false };
  }

  const environmentDefault = env.defaultOn.includes(feature.id);
  if (env.locked.includes(feature.id)) {
    return { enabled: environmentDefault, locked: true, available: true };
  }

  return { enabled: storedChoice ?? environmentDefault, locked: false, available: true };
}

export function findMisconfiguredExperimentalIds(
  env: ExperimentalFeatureEnv,
  features: readonly ExperimentalFeature[] = EXPERIMENTAL_FEATURE_LIST,
): ExperimentalFeatureId[] {
  return features
    .filter((feature) => feature.gated && !env.available.includes(feature.id))
    .filter((feature) => env.defaultOn.includes(feature.id) || env.locked.includes(feature.id))
    .map((feature) => feature.id);
}

export function findUnknownExperimentalIds(
  env: ExperimentalFeatureEnv,
  features: readonly ExperimentalFeature[] = EXPERIMENTAL_FEATURE_LIST,
): string[] {
  const known = new Set<string>(features.map((feature) => feature.id));

  return [...new Set([...env.available, ...env.defaultOn, ...env.locked])].filter((id) => !known.has(id));
}

export function warnAboutMisconfiguredExperimentalIds(
  env: ExperimentalFeatureEnv,
  features: readonly ExperimentalFeature[] = EXPERIMENTAL_FEATURE_LIST,
): void {
  if (import.meta.env.VITEST) return;

  for (const id of findMisconfiguredExperimentalIds(env, features)) {
    if (!claimReport(id)) continue;
    console.error(
      `Experimental feature "${id}" is configured in VITE_EXPERIMENTAL_DEFAULT_ON or VITE_EXPERIMENTAL_LOCKED but missing from VITE_EXPERIMENTAL_AVAILABLE, so it stays hidden.`,
    );
  }

  for (const id of findUnknownExperimentalIds(env, features)) {
    if (!claimReport(id)) continue;
    console.error(
      `Experimental feature id "${id}" is listed in ${variablesListing(env, id).join(" and ")} but matches no registered feature, so it has no effect.`,
    );
  }
}

const EXPERIMENTAL_ENV_VARIABLES = [
  ["available", "VITE_EXPERIMENTAL_AVAILABLE"],
  ["defaultOn", "VITE_EXPERIMENTAL_DEFAULT_ON"],
  ["locked", "VITE_EXPERIMENTAL_LOCKED"],
] as const satisfies readonly (readonly [keyof ExperimentalFeatureEnv, string])[];

// Resolution runs on every render; without this the console fills with the same misconfiguration.
const reportedIds = new Set<string>();

function claimReport(id: string): boolean {
  if (reportedIds.has(id)) return false;
  reportedIds.add(id);
  return true;
}

function variablesListing(env: ExperimentalFeatureEnv, id: string): string[] {
  return EXPERIMENTAL_ENV_VARIABLES.filter(([key]) => env[key].includes(id)).map(([, name]) => name);
}

function parseIdList(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}
